import { createContext, useContext, type ReactNode } from "react";

export type ShortcutDefinition = {
  id: string;
  title: string;
  group: string;
  defaultShortcut: string | null;
  scope: "app" | "editor";
  allowInEditor?: boolean;
};
export type ShortcutOverrides = Record<string, string | null>;
export type ShortcutPreferences = {
  definitions: readonly ShortcutDefinition[];
  overrides: ShortcutOverrides;
  save: (overrides: ShortcutOverrides) => Promise<void>;
};
const ShortcutContext = createContext<ShortcutPreferences>({
  definitions: [],
  overrides: {},
  save: async () => {},
});

export function ShortcutProvider({
  children,
  ...value
}: ShortcutPreferences & { children: ReactNode }) {
  return <ShortcutContext.Provider value={value}>{children}</ShortcutContext.Provider>;
}

export function useShortcutPreferences() {
  return useContext(ShortcutContext);
}

export function resolveShortcut(
  preferences: Pick<ShortcutPreferences, "definitions" | "overrides">,
  id: string,
): string | undefined {
  const value = Object.hasOwn(preferences.overrides, id)
    ? preferences.overrides[id]
    : preferences.definitions.find((definition) => definition.id === id)?.defaultShortcut;
  return value ?? undefined;
}

export function useShortcut(id: string): string | undefined {
  return resolveShortcut(useShortcutPreferences(), id);
}
